/**
 * Service Flow Context
 * Manages the flow of service requests → quotes → work orders
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export interface ServiceRequest {
  id: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  type: string;
  description: string;
  urgency: "low" | "medium" | "high";
  preferredDate: string;
  status: "pending" | "reviewing" | "approved" | "scheduled" | "rejected";
  createdAt: Date;
}

export interface Quote {
  id: string;
  requestId?: string;
  serviceRequestApiId?: number;
  clientId: string;
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  serviceType: string;
  description: string;
  preferredDate: string;
  amount: number;
  status: "draft" | "sent" | "accepted" | "rejected" | "converted";
  createdAt: Date;
  validUntil: Date;
}

export interface WorkOrder {
  id: string;
  quoteId?: string;
  requestId?: string;
  clientName: string;
  serviceType: string;
  description: string;
  scheduledDate: string;
  status: "pending" | "inProgress" | "completed";
  assignedTo?: string;
}

interface ServiceFlowContextState {
  pendingQuote: Quote | null;
  setPendingQuote: (quote: Quote | null) => void;
  createQuoteFromRequest: (request: ServiceRequest) => Quote;
  pendingWorkOrder: WorkOrder | null;
  setPendingWorkOrder: (workOrder: WorkOrder | null) => void;
  createWorkOrderFromQuote: (quote: Quote) => WorkOrder;
}

const ServiceFlowContext = createContext<ServiceFlowContextState | null>(null);

interface ServiceFlowProviderProps {
  children: ReactNode;
}

export function ServiceFlowProvider({
  children,
}: ServiceFlowProviderProps): React.ReactElement {
  const [pendingQuote, setPendingQuote] = useState<Quote | null>(null);
  const [pendingWorkOrder, setPendingWorkOrder] = useState<WorkOrder | null>(
    null,
  );

  const createQuoteFromRequest = useCallback(
    (request: ServiceRequest): Quote => {
      const quoteId = `PRE-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, "0")}`;

      const validUntil = new Date();
      validUntil.setDate(validUntil.getDate() + 30); // Valid for 30 days

      const quote: Quote = {
        id: quoteId,
        requestId: request.id,
        clientId: request.clientId,
        clientName: request.clientName,
        clientEmail: request.clientEmail,
        clientPhone: request.clientPhone,
        serviceType: request.type,
        description: request.description,
        preferredDate: request.preferredDate,
        amount: 0, // To be filled in quotes page
        status: "draft",
        createdAt: new Date(),
        validUntil,
      };

      setPendingQuote(quote);
      return quote;
    },
    [setPendingQuote],
  );

  const createWorkOrderFromQuote = useCallback(
    (quote: Quote): WorkOrder => {
      const workOrderId = `OT-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, "0")}`;

      const workOrder: WorkOrder = {
        id: workOrderId,
        quoteId: quote.id,
        requestId: quote.requestId,
        clientName: quote.clientName,
        serviceType: quote.serviceType,
        description: quote.description,
        scheduledDate: quote.preferredDate,
        status: "pending",
      };

      setPendingWorkOrder(workOrder);
      return workOrder;
    },
    [setPendingWorkOrder],
  );

  const value: ServiceFlowContextState = {
    pendingQuote,
    setPendingQuote,
    createQuoteFromRequest,
    pendingWorkOrder,
    setPendingWorkOrder,
    createWorkOrderFromQuote,
  };

  return (
    <ServiceFlowContext.Provider value={value}>
      {children}
    </ServiceFlowContext.Provider>
  );
}

export function useServiceFlow(): ServiceFlowContextState {
  const context = useContext(ServiceFlowContext);
  if (!context) {
    throw new Error("useServiceFlow must be used within ServiceFlowProvider");
  }
  return context;
}
